import {ArrowPathIcon, CheckCircleIcon, DocumentTextIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Select,
    Option,
    Typography,
    IconButton
} from "@material-tailwind/react";
import {useEffect, useMemo, useState} from "react";
import axios from "axios";

export default function FinancialReportCard() {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const years = useMemo(() => Array.from(
        { length: new Date().getFullYear() - 2023 },
        (_, i) => 2024 + i
    ), []);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const openSelectionDialog = () => {
        setErrorMessage("");
        setIsDialogOpen(true);
    };

    const closeSelectionDialog = () => {
        setIsDialogOpen(false);
        setErrorMessage("");
    };

    const closeReportDialog = () => {
        setIsReportDialogOpen(false);
    };

    const changePeriod = () => {
        setIsReportDialogOpen(false);
        setIsDialogOpen(true);
    };

    const fetchFinancialReport = async () => {
        setIsLoading(true);
        setErrorMessage("");

        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
        const token = localStorage.getItem("token");

        try {
            const response = await axios.get(
                `${API_URL}/purchased_items/financial_report`,
                {
                    params: {
                        year: selectedYear,
                        month: selectedMonth
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            setReportData(response.data);
            setIsDialogOpen(false);
            setIsReportDialogOpen(true);
        } catch (err) {
            console.error("Error fetching financial report:", err);
            setErrorMessage("Financial report could not be generated for the selected period.");
        } finally {
            setIsLoading(false);
        }
    };

    const getFiniteNumber = (value) => {
        return Number.isFinite(value) ? value : 0;
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('sr-RS', {
            style: 'currency',
            currency: 'RSD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(getFiniteNumber(value));
    };

    const formatPercentage = (value) => {
        return Number.isFinite(value) ? `${value.toFixed(2)}%` : '/';
    };

    const formatPaymentType = (paymentType) => {
        switch (paymentType) {
            case 'IN_PERSON':
                return 'In person';
            case 'DIRECT_PAYMENT_N':
                return 'Direct payment N';
            case 'DIRECT_PAYMENT_S':
                return 'Direct payment S';
            case 'DIRECT_PAYMENT_B':
                return 'Direct payment B';
            case 'DEXPRESS':
                return 'DExpress';
            case 'POSTEXPRESS':
                return 'PostExpress';
            default:
                return paymentType;
        }
    };

    const amountColor = (value, fallback = 'text-gray-900 dark:text-gray-200') => {
        const amount = getFiniteNumber(value);
        if (amount > 0) return 'text-green-600';
        if (amount < 0) return 'text-red-600';
        return fallback;
    };

    const paymentBreakdown = useMemo(() => {
        if (!reportData?.paymentTypeBreakdown) return [];

        return Object.entries(reportData.paymentTypeBreakdown)
            .filter(([, amount]) => Number.isFinite(amount) && amount > 0)
            .sort((a, b) => b[1] - a[1]);
    }, [reportData]);

    const summaryMetrics = reportData ? [
        {
            label: 'Net Profit',
            value: formatCurrency(reportData.netProfit),
            valueClassName: amountColor(reportData.netProfit),
        },
        {
            label: 'Sales Revenue',
            value: formatCurrency(reportData.totalSalesRevenue),
            valueClassName: 'text-green-600',
        },
        {
            label: 'Gross Profit',
            value: formatCurrency(reportData.grossProfit),
            valueClassName: amountColor(reportData.grossProfit),
        },
        {
            label: 'Avg. Margin',
            value: formatPercentage(reportData.averageMargin),
            valueClassName: 'text-cyan-800 dark:text-cyan-200',
        },
    ] : [];

    const reportSections = reportData ? [
        {
            title: 'Inventory',
            rows: [
                { label: 'Inventory Value', value: formatCurrency(reportData.inventoryValue), valueClassName: 'text-green-600' },
                { label: 'Total Personal Cost', value: formatCurrency(reportData.totalPersonalCost), valueClassName: 'text-red-600' },
                { label: 'Total Inventory Cost', value: formatCurrency(reportData.totalInventoryCost), valueClassName: 'text-red-600' },
            ],
        },
        {
            title: 'Sales',
            rows: [
                { label: 'Total Sales Revenue', value: formatCurrency(reportData.totalSalesRevenue), valueClassName: 'text-green-600' },
                { label: 'Cost of Goods Sold', value: formatCurrency(reportData.costOfGoodsSold), valueClassName: 'text-red-600' },
            ],
        },
        {
            title: 'Profit',
            rows: [
                { label: 'Gross Profit', value: formatCurrency(reportData.grossProfit), valueClassName: amountColor(reportData.grossProfit) },
                { label: 'Operating Expenses', value: formatCurrency(reportData.operatingExpenses), valueClassName: 'text-red-600' },
                { label: 'Net Profit', value: formatCurrency(reportData.netProfit), valueClassName: amountColor(reportData.netProfit) },
                { label: 'Avg. Profit Margin', value: formatPercentage(reportData.averageMargin), valueClassName: 'text-cyan-800 dark:text-cyan-200' },
            ],
        },
    ] : [];

    useEffect(() => {
        if (isDialogOpen || isReportDialogOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isDialogOpen, isReportDialogOpen]);

    const ReportSection = ({ title, rows }) => (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-dark-800">
            <div className="px-4 py-2 bg-gray-50 dark:bg-dark-700">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</span>
            </div>
            {rows.map(({ label, value, valueClassName }) => (
                <div key={label} className="flex items-center justify-between gap-4 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/50">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
                    <span className={`text-sm font-semibold tabular-nums text-right ${valueClassName}`}>{value}</span>
                </div>
            ))}
        </div>
    );

    return (
        <>
            <li className="py-1.5 cursor-pointer" onClick={openSelectionDialog}>
                <div className="flex items-center space-x-4 text-left p-0 focus:bg-green-50 dark:focus:bg-cyan-900 hover:bg-green-50 dark:hover:bg-cyan-900">
                    <div className="flex-1 min-w-0">
                        <p className="text-md font-medium text-gray-900 truncate dark:text-white">
                            Financial Report
                        </p>
                        <div className="flex items-center space-x-2 mt-3">
                            <DocumentTextIcon className="w-4 h-4 text-cyan-800 dark:text-cyan-200" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Generate financial report
                            </p>
                        </div>
                    </div>
                    <div className="ml-2">
                        <DocumentTextIcon className="h-5 w-5 text-cyan-800 dark:text-cyan-200" />
                    </div>
                </div>
            </li>

            <Dialog size="xs" open={isDialogOpen} handler={closeSelectionDialog} className="p-4 dark:bg-dark-800">
                <DialogHeader className="relative m-0 block pb-2">
                    <Typography variant="h5" color="blue-gray" className="dark:text-gray-200">Financial Report</Typography>
                    <Typography className="text-sm font-normal text-gray-500 dark:text-gray-400">Select period</Typography>
                    <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={closeSelectionDialog}>
                        <XMarkIcon className="h-4 w-4 stroke-2" />
                    </IconButton>
                </DialogHeader>
                <DialogBody className="space-y-3 py-3">
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-1.5 font-bold dark:text-gray-300">Year</Typography>
                        <Select
                            label="Select Year"
                            value={String(selectedYear)}
                            onChange={(value) => setSelectedYear(Number(value))}
                        >
                            {years.map(year => <Option key={year} value={String(year)}>{year}</Option>)}
                        </Select>
                    </div>
                    <div>
                        <Typography variant="small" color="blue-gray" className="mb-1.5 font-bold dark:text-gray-300">Month</Typography>
                        <Select
                            label="Select Month"
                            value={String(selectedMonth)}
                            onChange={(value) => setSelectedMonth(Number(value))}
                            displayValue={(value) => months[Number(value) - 1] || 'Select Month'}
                        >
                            {months.map((month, index) => <Option key={month} value={String(index + 1)}>{month}</Option>)}
                        </Select>
                    </div>
                    {errorMessage && (
                        <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
                            {errorMessage}
                        </div>
                    )}
                </DialogBody>
                <DialogFooter className="gap-2">
                    <button
                        onClick={closeSelectionDialog}
                        disabled={isLoading}
                        className="mv-secondary-button"
                    >
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                    </button>
                    <button
                        onClick={fetchFinancialReport}
                        disabled={isLoading}
                        className="mv-primary-button"
                    >
                        {!isLoading && <DocumentTextIcon className="h-4 w-4" />}
                        {isLoading && (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                        )}
                        {isLoading ? 'Generating...' : 'Generate'}
                    </button>
                </DialogFooter>
            </Dialog>

            <Dialog
                size="xl"
                open={isReportDialogOpen}
                handler={closeReportDialog}
                className="p-4 max-h-[calc(100vh-4rem)] overflow-y-auto dark:bg-dark-800"
            >
                <DialogHeader className="relative m-0 block pb-2">
                    <Typography variant="h5" color="blue-gray" className="dark:text-gray-200">Financial Report</Typography>
                    <Typography className="text-sm font-normal text-gray-500 dark:text-gray-400">
                        {reportData?.monthYear || 'Selected Period'}
                    </Typography>
                    <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={closeReportDialog}>
                        <XMarkIcon className="h-4 w-4 stroke-2" />
                    </IconButton>
                </DialogHeader>
                <DialogBody className="py-3">
                    {reportData && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                                {summaryMetrics.map(({ label, value, valueClassName }) => (
                                    <div key={label} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-800 px-4 py-3">
                                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</p>
                                        <p className={`mt-1 text-lg font-semibold tabular-nums ${valueClassName}`}>{value}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                                {reportSections.map(({ title, rows }) => (
                                    <ReportSection key={title} title={title} rows={rows} />
                                ))}

                                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-dark-800">
                                    <div className="px-4 py-2 bg-gray-50 dark:bg-dark-700">
                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Payment Breakdown</span>
                                    </div>
                                    {paymentBreakdown.length > 0 ? (
                                        paymentBreakdown.map(([paymentType, amount]) => (
                                            <div key={paymentType} className="flex items-center justify-between gap-4 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/50">
                                                <span className="text-sm text-gray-600 dark:text-gray-400">{formatPaymentType(paymentType)}</span>
                                                <span className="text-sm font-semibold text-green-600 tabular-nums text-right">{formatCurrency(amount)}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-4 py-6 border-t border-gray-100 dark:border-gray-700/50 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No payments for this period
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogBody>
                <DialogFooter className="gap-2 pt-3">
                    <button
                        onClick={changePeriod}
                        className="mv-secondary-button"
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                        Change period
                    </button>
                    <button
                        onClick={closeReportDialog}
                        className="mv-primary-button"
                    >
                        <CheckCircleIcon className="h-4 w-4" />
                        Done
                    </button>
                </DialogFooter>
            </Dialog>
        </>
    );
}
